import React from "react";
import { Button } from "../../components/Button/Button";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Modal } from "../../components/Modal/Modal";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { WarningIcon } from "../../theme/svg/WarningIcon";
import {
  buildDrawingRows,
  DrawingSearchResultData,
} from "./drawingSearchPresentation";
import styles from "./DrawingSearchResultModal.module.scss";

// several lines can carry the same code; name each one once
const unique = (codes: string[]) => Array.from(new Set(codes));

export interface DrawingSearchResultModalProps {
  result?: DrawingSearchResultData | null;
  error?: string;
  onClose(): void;
}

export const DrawingSearchResultModal: React.FC<DrawingSearchResultModalProps> =
  ({ result, error, onClose }) => {
    const rows = result ? buildDrawingRows(result) : [];

    return (
      <Modal
        title="Drawings"
        onCloseRequested={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        <div className={styles.wrap}>
          <ErrorMessage error={error} />

          {result && result.incomplete && (
            <p className={styles.warning}>
              The search did not finish within the time limit. Press Drawings
              again to continue; nothing will be duplicated.
            </p>
          )}

          {rows.map((row) => (
            <div key={row.key} className={styles.row}>
              <div className={styles.code}>
                <strong>{row.code}</strong>
                {row.missingFormats.length > 0 && (
                  <Tooltip
                    reference={
                      <WarningIcon
                        className={styles.missingIcon}
                        aria-label="Missing drawing files"
                      />
                    }
                  >
                    <span data-testid="drawing-missing">
                      {`Not found (every drawing needs DWG, PDF and STEP):\n${row.missingFormats
                        .map(
                          (missing) =>
                            `${missing.number}: ${missing.formats.join(", ")}`,
                        )
                        .join("\n")}`}
                    </span>
                  </Tooltip>
                )}
              </div>
              <div>
                {row.attached.length} drawing(s) on the line
                {row.attached.length > 0 && `: ${row.attached.join(", ")}`}
              </div>
              {row.subDrawings.length > 0 && (
                <div>
                  {row.subDrawings.length} sub-drawing(s):{" "}
                  {row.subDrawings.join(", ")}
                </div>
              )}
              {row.truncated && (
                <div className={styles.note}>
                  Drawing limit for this line reached; more drawings may exist.
                </div>
              )}
              {row.failures.map((failure, index) => (
                <div
                  key={`${failure.filename}-${index}`}
                  className={failure.isNote ? styles.note : styles.failure}
                  data-testid={
                    failure.isNote ? "drawing-failure-note" : "drawing-failure"
                  }
                >
                  {failure.text}
                </div>
              ))}
            </div>
          ))}

          {result && result.notFoundCodes.length > 0 && (
            <p className={styles.notFound}>
              No drawing found for: {unique(result.notFoundCodes).join(", ")}
            </p>
          )}

          {result && result.skippedCodes.length > 0 && (
            <p className={styles.note}>
              Not searched, not a product code:{" "}
              {unique(result.skippedCodes).join(", ")}
            </p>
          )}

          {result && result.conflicts.length > 0 && (
            <div className={styles.failure} data-testid="drawing-conflicts">
              Not searched, more than one code on the line. Keep one code in the
              name or Item code and press Drawings again:
              <ul>
                {result.conflicts.map((conflict) => (
                  <li key={conflict.purchaseRequestItemId}>
                    {conflict.name} ({conflict.codes.join(", ")})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result &&
            rows.length === 0 &&
            result.notFoundCodes.length === 0 &&
            result.skippedCodes.length === 0 &&
            result.conflicts.length === 0 &&
            !error && <p>No drawings found.</p>}
        </div>
      </Modal>
    );
  };
